import { Injectable } from '@angular/core';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { UrlQueryService } from 'services/url-query-service';
import { UserDb } from 'services/user-db.service';
import {
  GetCoachBillingStateResult,
  CoachStartPaddleCheckoutResult,
} from '@cf-shared/coach-billing-interface';
import { GetCoachHistoryResult, GetCoachInvoiceUrlResult } from '@cf-shared/coach-history-interface';

/**
 * Client-side façade for the Coach Billing dashboard.
 *
 * Wraps the cloud functions the dashboard talks to:
 *   - `getCoachBillingState`: read the fold-derived balance + history
 *   - `coachStartPaddleCheckout`: begin become-a-coach on the web
 *   - `getCoachHistory` / `getCoachInvoiceUrl`: the Clients Over Time and Past
 *     Payments cards (#6768)
 *
 * While support is acting as a coach (`isImpersonating()`), the reads go to
 * the officer-only versions with the impersonated coach's uid: the Firebase
 * token still carries the support person's own uid, so the coach versions
 * would show the support person. See design-docs/coach-billing-analytics.md,
 * "Phase 3 — the coach's view".
 *
 * See `design-docs/coach-slot-pricing.md` for the architecture and
 * `plans/coach-slot-pricing-phase1.md` (#3666) §R12 for dashboard
 * requirements.
 *
 * No client-side caching for Phase 1 — the dashboard page calls
 * `loadStateAsync` on `ionViewWillEnter` and on pull-to-refresh. The
 * authoritative state lives in the server-side `log_coach_billing`
 * event log + `coachBillingData` cache; round-tripping ensures the
 * coach always sees a fresh view, including the very latest
 * monthly-statement row written by the cron.
 */
@Injectable({ providedIn: 'root' })
export class CoachBillingService {
  constructor(
    private cloudFunctions: CloudFunctionsService,
    private urlQuery: UrlQueryService,
    private userDb: UserDb,
  ) {}

  /** Fetch the latest dashboard state and history. The dashboard
   *  treats network/server errors as a load failure and falls back
   *  to the previous successful payload — see
   *  `coach-billing-page.ts`'s error handling. */
  loadStateAsync(): Promise<GetCoachBillingStateResult> {
    const coachUid = this.impersonatedCoachUid();
    return coachUid
      ? this.cloudFunctions.adminGetCoachBillingStateAsync({ coachUid })
      : this.cloudFunctions.getCoachBillingStateAsync({});
  }

  /** The coach's recorded history for the Clients Over Time and Past Payments
   *  cards (#6768), oldest first. A failure throws; the page shows its inline
   *  error and keeps the plan cards. */
  loadHistoryAsync(): Promise<GetCoachHistoryResult> {
    const coachUid = this.impersonatedCoachUid();
    return coachUid
      ? this.cloudFunctions.adminGetCoachHistoryAsync({ coachUid })
      : this.cloudFunctions.getCoachHistoryAsync({});
  }

  /** Paddle's invoice link for one Paddle charge, valid for an hour. The link
   *  is a credential: open it and drop it, never log or store it. */
  getInvoiceUrlAsync(chargeId: string): Promise<GetCoachInvoiceUrlResult> {
    const coachUid = this.impersonatedCoachUid();
    return coachUid
      ? this.cloudFunctions.adminGetCoachInvoiceUrlAsync({ coachUid, chargeId })
      : this.cloudFunctions.getCoachInvoiceUrlAsync({ chargeId });
  }

  /** The coach support is acting as, or undefined when the signed-in user is
   *  looking at their own page. Throws when an impersonation session has no
   *  uid, which would otherwise show the support person's own billing. */
  private impersonatedCoachUid(): string | undefined {
    if (!this.userDb.isImpersonating()) {
      return undefined;
    }
    const uid = this.userDb.getImpersonationUserAuth()?.uid;
    if (!uid) {
      throw new Error('coach billing: impersonating, but the impersonated account has no uid');
    }
    return uid;
  }

  /** Begin become-a-coach on the web (Paddle rail, Phase 4). The server runs the
   *  authoritative cross-provider gate; on pass it returns the Paddle checkout
   *  config so the caller can open the coaching checkout for `priceCoachSeat` at
   *  `quantity = 1`. On a cross-provider conflict it returns
   *  `{ error: 'cross_provider_conflict' }`. We send the same base64 tracking
   *  blob the subscribe page sends, so the server reads its `e2e-test` marker and
   *  picks the Paddle sandbox vs live environment (an E2E run never spends real
   *  money). */
  startPaddleCheckoutAsync(): Promise<CoachStartPaddleCheckoutResult> {
    return this.cloudFunctions.coachStartPaddleCheckoutAsync({ telemetryData: this.urlQuery.getTrackingDataBase64() });
  }
}
